import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, AlertCircle, Clock, Monitor, Smartphone, Tablet, LogIn, LogOut, Key, Mail, RefreshCw, Trash2, Globe, Lock, ChevronRight, Activity, XCircle, Info } from 'lucide-react';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  getSecurityLogs,
  getUserSessions,
  revokeSession,
  revokeAllOtherSessions,
  logSecurityEvent,
} from '../../services/securityService';
import Icon from '../../components/AppIcon';


const EVENT_LABELS = {
  login: { label: 'Sign In', icon: LogIn, color: 'text-success' },
  logout: { label: 'Sign Out', icon: LogOut, color: 'text-muted-foreground' },
  password_change: { label: 'Password Changed', icon: Key, color: 'text-warning' },
  email_change: { label: 'Email Updated', icon: Mail, color: 'text-primary' },
  login_failed: { label: 'Failed Sign In', icon: XCircle, color: 'text-destructive' },
  password_reset: { label: 'Password Reset', icon: Key, color: 'text-warning' },
  email_verification: { label: 'Email Verified', icon: CheckCircle, color: 'text-success' },
  session_revoked: { label: 'Session Revoked', icon: Shield, color: 'text-destructive' },
  signup: { label: 'Account Created', icon: CheckCircle, color: 'text-success' },
};

const DEVICE_ICONS = {
  mobile: Smartphone,
  tablet: Tablet,
  desktop: Monitor,
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return 'Unknown';
  return new Date(dateStr)?.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function SecurityManagement() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [auditLogs, setAuditLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokeLoading, setRevokeLoading] = useState(null);
  const [revokeAllLoading, setRevokeAllLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [logFilter, setLogFilter] = useState('all');

  const isEmailVerified = user?.email_confirmed_at != null;

  useEffect(() => {
    if (!user?.id) return;
    loadData();
  }, [user?.id]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(p => p - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const loadData = async () => {
    if (!user?.id) return;
    setLogsLoading(true);
    setSessionsLoading(true);

    const [logsResult, sessionsResult] = await Promise.all([
      getSecurityLogs(user?.id, 100),
      getUserSessions(user?.id),
    ]);

    setAuditLogs(logsResult?.data || []);
    setSessions(sessionsResult?.data || []);
    setLogsLoading(false);
    setSessionsLoading(false);
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await supabase?.auth?.resend({ type: 'signup', email: user?.email });
      await supabase?.functions?.invoke('send-verification-email', {
        body: { email: user?.email },
      });
      setResendSuccess(true);
      setResendCooldown(60);
    } catch (err) {
      console.warn('Resend failed:', err?.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokeLoading(sessionId);
    try {
      await revokeSession(sessionId);
      await logSecurityEvent(user?.id, 'session_revoked', 'success', { sessionId });
      setSessions(prev => prev?.filter(s => s?.id !== sessionId));
    } catch (err) {
      console.warn('Revoke session failed:', err?.message);
    } finally {
      setRevokeLoading(null);
    }
  };

  const handleRevokeAllOther = async () => {
    setRevokeAllLoading(true);
    try {
      await revokeAllOtherSessions(user?.id);
      await logSecurityEvent(user?.id, 'session_revoked', 'success', { scope: 'all_other' });
      setSessions(prev => prev?.filter(s => s?.is_current));
    } catch (err) {
      console.warn('Revoke all failed:', err?.message);
    } finally {
      setRevokeAllLoading(false);
    }
  };

  const filteredLogs = auditLogs?.filter(log => {
    if (logFilter === 'all') return true;
    if (logFilter === 'auth') return ['login', 'logout', 'login_failed', 'signup']?.includes(log?.event_type);
    if (logFilter === 'changes') return ['password_change', 'email_change', 'password_reset']?.includes(log?.event_type);
    if (logFilter === 'failed') return log?.event_status === 'failed' || log?.event_type === 'login_failed';
    return true;
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'sessions', label: 'Sessions', icon: Monitor },
    { id: 'activity', label: 'Activity Log', icon: Activity },
  ];

  // Stats for overview
  const recentFailedLogins = auditLogs?.filter(l =>
    l?.event_type === 'login_failed' &&
    new Date(l?.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  )?.length;

  const lastLogin = auditLogs?.find(l => l?.event_type === 'login');
  const activeSessions = sessions?.length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Security & Privacy</h1>
            <p className="text-muted-foreground text-sm">Manage your account security, sessions, and activity</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-muted rounded-xl p-1 mb-6">
          {tabs?.map(tab => {
            const Icon = tab?.icon;
            return (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab?.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Email Verification Status */}
            <div className={`bg-card rounded-2xl border p-5 ${isEmailVerified ? 'border-success/30' : 'border-warning/50'}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${isEmailVerified ? 'bg-success/10' : 'bg-warning/10'}`}>
                    {isEmailVerified
                      ? <CheckCircle className="w-5 h-5 text-success" />
                      : <AlertCircle className="w-5 h-5 text-warning" />
                    }
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Email Verification</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">{user?.email}</p>
                    {isEmailVerified ? (
                      <p className="text-sm text-success mt-1 flex items-center space-x-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span>Verified on {formatFullDate(user?.email_confirmed_at)}</span>
                      </p>
                    ) : (
                      <p className="text-sm text-warning mt-1">⚠️ Email not yet verified</p>
                    )}
                  </div>
                </div>
                {!isEmailVerified && (
                  <button
                    onClick={handleResendVerification}
                    disabled={resendCooldown > 0 || resendLoading}
                    className="flex items-center space-x-1.5 text-sm text-primary hover:text-primary/80 font-medium disabled:opacity-50 transition-colors"
                  >
                    {resendLoading ? (
                      <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span>{resendCooldown > 0 ? `${resendCooldown}s` : 'Resend'}</span>
                  </button>
                )}
              </div>
              {resendSuccess && (
                <div className="mt-3 p-2 bg-success/10 rounded-lg text-success text-sm flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Verification email sent! Check your inbox.</span>
                </div>
              )}
            </div>

            {/* Security Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-2xl font-bold text-foreground">{activeSessions}</div>
                <div className="text-xs text-muted-foreground mt-1">Active Sessions</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className={`text-2xl font-bold ${recentFailedLogins > 0 ? 'text-destructive' : 'text-success'}`}>
                  {recentFailedLogins}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Failed Logins (7d)</div>
              </div>
              <div className="bg-card rounded-xl border border-border p-4 text-center">
                <div className="text-sm font-semibold text-foreground">
                  {lastLogin ? formatRelativeTime(lastLogin?.created_at) : '—'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Last Sign In</div>
              </div>
            </div>

            {/* Security Checklist */}
            <div className="bg-card rounded-2xl border border-border p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
                <Lock className="w-4 h-4 text-primary" />
                <span>Security Checklist</span>
              </h3>
              <div className="space-y-3">
                {[
                  {
                    label: 'Email verified',
                    done: isEmailVerified,
                    action: !isEmailVerified ? handleResendVerification : null,
                    actionLabel: 'Verify now',
                  },
                  {
                    label: 'Strong password set',
                    done: true,
                    info: 'Use a unique password with 8+ characters',
                  },
                  {
                    label: 'Review active sessions',
                    done: activeSessions <= 1,
                    action: () => setActiveTab('sessions'),
                    actionLabel: 'Review',
                  },
                  {
                    label: 'No suspicious login attempts',
                    done: recentFailedLogins === 0,
                    action: recentFailedLogins > 0 ? () => setActiveTab('activity') : null,
                    actionLabel: 'View activity',
                  },
                ]?.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {item?.done
                        ? <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                        : <AlertCircle className="w-4 h-4 text-warning flex-shrink-0" />
                      }
                      <span className={`text-sm ${item?.done ? 'text-foreground' : 'text-warning'}`}>
                        {item?.label}
                      </span>
                    </div>
                    {item?.action && (
                      <button
                        onClick={item?.action}
                        className="text-xs text-primary hover:text-primary/80 font-medium"
                      >
                        {item?.actionLabel}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-card rounded-2xl border border-border divide-y divide-border">
              <button
                onClick={() => navigate('/user-profile', { state: { activeTab: 'account' } })}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Key className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Change Password</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => setActiveTab('sessions')}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Monitor className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Manage Sessions</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Activity className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">View Activity Log</span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        {/* ── SESSIONS TAB ── */}
        {activeTab === 'sessions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-foreground">Active Sessions</h2>
                <p className="text-sm text-muted-foreground">Devices currently signed in to your account</p>
              </div>
              {sessions?.filter(s => !s?.is_current)?.length > 0 && (
                <button
                  onClick={handleRevokeAllOther}
                  disabled={revokeAllLoading}
                  className="text-sm text-destructive hover:text-destructive/80 font-medium flex items-center space-x-1.5 disabled:opacity-50"
                >
                  {revokeAllLoading
                    ? <div className="animate-spin h-3.5 w-3.5 border-2 border-destructive border-t-transparent rounded-full" />
                    : <Trash2 className="w-3.5 h-3.5" />
                  }
                  <span>Revoke All Others</span>
                </button>
              )}
            </div>

            {sessionsLoading ? (
              <div className="space-y-3">
                {[1, 2]?.map(i => (
                  <div key={i} className="bg-card rounded-xl border border-border p-4 animate-pulse">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-muted rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-1/3" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : sessions?.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-8 text-center">
                <Monitor className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No session records found.</p>
                <p className="text-muted-foreground text-xs mt-1">Sessions are recorded when you sign in.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions?.map(session => {
                  const DeviceIcon = DEVICE_ICONS?.[session?.device_type] || Monitor;
                  return (
                    <div
                      key={session?.id}
                      className={`bg-card rounded-xl border p-4 ${session?.is_current ? 'border-primary/30 bg-primary/5' : 'border-border'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${session?.is_current ? 'bg-primary/10' : 'bg-muted'}`}>
                            <DeviceIcon className={`w-5 h-5 ${session?.is_current ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-foreground text-sm">
                                {session?.device_name || `${session?.browser || 'Unknown'} on ${session?.os || 'Unknown'}`}
                              </span>
                              {session?.is_current && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                                  Current
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-3 mt-1 text-xs text-muted-foreground">
                              {session?.ip_address && (
                                <span className="flex items-center space-x-1">
                                  <Globe className="w-3 h-3" />
                                  <span>{session?.ip_address}</span>
                                </span>
                              )}
                              <span className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>Active {formatRelativeTime(session?.last_active_at)}</span>
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Started {formatFullDate(session?.created_at)}
                            </p>
                          </div>
                        </div>
                        {!session?.is_current && (
                          <button
                            onClick={() => handleRevokeSession(session?.id)}
                            disabled={revokeLoading === session?.id}
                            className="flex items-center space-x-1.5 text-xs text-destructive hover:text-destructive/80 font-medium disabled:opacity-50 transition-colors ml-2 flex-shrink-0"
                          >
                            {revokeLoading === session?.id
                              ? <div className="animate-spin h-3.5 w-3.5 border-2 border-destructive border-t-transparent rounded-full" />
                              : <XCircle className="w-3.5 h-3.5" />
                            }
                            <span>Revoke</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Sessions are created each time you sign in. Revoking a session will sign out that device on next activity.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── ACTIVITY LOG TAB ── */}
        {activeTab === 'activity' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="font-semibold text-foreground">Activity Log</h2>
                <p className="text-sm text-muted-foreground">Security events for your account</p>
              </div>
              <button
                onClick={loadData}
                className="flex items-center space-x-1.5 text-sm text-primary hover:text-primary/80 font-medium"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Refresh</span>
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex space-x-2 overflow-x-auto pb-1">
              {[
                { id: 'all', label: 'All Events' },
                { id: 'auth', label: 'Sign In/Out' },
                { id: 'changes', label: 'Changes' },
                { id: 'failed', label: 'Failed' },
              ]?.map(f => (
                <button
                  key={f?.id}
                  onClick={() => setLogFilter(f?.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    logFilter === f?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f?.label}
                </button>
              ))}
            </div>

            {logsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4]?.map(i => (
                  <div key={i} className="bg-card rounded-xl border border-border p-4 animate-pulse">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-muted rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3.5 bg-muted rounded w-1/4" />
                        <div className="h-3 bg-muted rounded w-1/3" />
                      </div>
                      <div className="h-3 bg-muted rounded w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredLogs?.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-8 text-center">
                <Activity className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No activity records found.</p>
                <p className="text-muted-foreground text-xs mt-1">Security events will appear here as you use your account.</p>
              </div>
            ) : (
              <div className="bg-card rounded-2xl border border-border divide-y divide-border overflow-hidden">
                {filteredLogs?.map(log => {
                  const eventInfo = EVENT_LABELS?.[log?.event_type] || {
                    label: log?.event_type?.replace(/_/g, ' '),
                    icon: Activity,
                    color: 'text-muted-foreground',
                  };
                  const EventIcon = eventInfo?.icon;
                  const isFailed = log?.event_status === 'failed' || log?.event_type === 'login_failed';
                  const deviceInfo = log?.device_info || {};

                  return (
                    <div key={log?.id} className="p-4 hover:bg-muted/30 transition-colors">
                      <div className="flex items-start space-x-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${isFailed ? 'bg-destructive/10' : 'bg-muted'}`}>
                          <EventIcon className={`w-4 h-4 ${isFailed ? 'text-destructive' : eventInfo?.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                            <span className="text-sm font-medium text-foreground">{eventInfo?.label}</span>
                            {isFailed && (
                              <span className="text-xs bg-destructive/10 text-destructive px-1.5 py-0.5 rounded font-medium">
                                Failed
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-3 mt-1 text-xs text-muted-foreground flex-wrap gap-y-0.5">
                            {deviceInfo?.browser && (
                              <span>{deviceInfo?.browser} on {deviceInfo?.os}</span>
                            )}
                            {log?.ip_address && (
                              <span className="flex items-center space-x-1">
                                <Globe className="w-3 h-3" />
                                <span>{log?.ip_address}</span>
                              </span>
                            )}
                          </div>
                          {log?.metadata && Object.keys(log?.metadata)?.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {Object.entries(log?.metadata)
                                ?.filter(([k]) => !['method']?.includes(k))
                                ?.map(([k, v]) => `${k}: ${v}`)
                                ?.join(' · ')}
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex-shrink-0 text-right">
                          <div>{formatRelativeTime(log?.created_at)}</div>
                          <div className="text-[10px] mt-0.5 hidden sm:block">
                            {new Date(log?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
